/**
 * @zendir/ui - StatusIndicator Component (Pure React)
 *
 * Displays status symbols following Astro UX Design System patterns.
 * Uses the same 6-level status system: off, standby, normal, caution, serious, critical.
 *
 * Shapes per Astro UX (https://www.astrouxds.com/patterns/status-system/):
 * - Off: Circle (grey)
 * - Standby: Ring (cyan)
 * - Normal: Circle (green)
 * - Caution: Square (yellow)
 * - Serious: Diamond (orange)
 * - Critical: Triangle pointing down (red)
 *
 * Features:
 * - Dual coding (color + shape) for accessibility (WCAG 2.1 AA)
 * - Animated pulse for active states
 * - Light theme border support
 * - Theme-connected via useTheme()
 * - Keyboard accessible
 * - Screen reader support
 */

import React, { memo } from "react";
import { useTheme } from "../theme";
import { classNames, type StatusLevel } from "../utils";

export type StatusVariant = StatusLevel;

export interface StatusIndicatorProps {
  /** Status variant - determines color and shape */
  status: StatusVariant;
  /** Status label text */
  label?: string;
  /** Smaller size variant */
  small?: boolean;
  /** Show pulse animation for active states */
  pulse?: boolean;
  /** Show border (required for light theme per Astro UX) */
  bordered?: boolean;
  /** Custom className */
  className?: string;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Astro UX Status Colors (Dark Theme)
 * https://www.astrouxds.com/patterns/status-system/
 */
const STATUS_COLORS_DARK: Record<StatusVariant, string> = {
  off: "#a4abb6",
  standby: "#2dccff",
  normal: "#56f000",
  caution: "#fce83a",
  serious: "#ffb302",
  critical: "#ff3838",
};

/**
 * Astro UX Status Colors (Light Theme - Fill)
 * Requires border for WCAG AA compliance
 */
const STATUS_COLORS_LIGHT_FILL: Record<StatusVariant, string> = {
  off: "#7b8089",
  standby: "#64d9ff",
  normal: "#00e200",
  caution: "#fad800",
  serious: "#ffaf3d",
  critical: "#ff2a04",
};

/**
 * Astro UX Status Colors (Light Theme - Border)
 * Required for light backgrounds per Astro UX
 */
const STATUS_COLORS_LIGHT_BORDER: Record<StatusVariant, string> = {
  off: "#3c3e42",
  standby: "#285766",
  normal: "#005a00",
  caution: "#645600",
  serious: "#664618",
  critical: "#661102",
};

const STATUS_ARIA_LABELS: Record<StatusVariant, string> = {
  off: "Status: Off",
  standby: "Status: Standby",
  normal: "Status: Normal",
  caution: "Status: Caution",
  serious: "Status: Serious",
  critical: "Status: Critical",
};

/**
 * Render Astro-compliant status shape
 * Shapes per https://www.astrouxds.com/patterns/status-system/
 */
const renderStatusShape = (
  status: StatusVariant,
  size: number,
  fillColor: string,
  borderColor?: string,
): React.ReactNode => {
  const hasBorder = !!borderColor;
  const strokeWidth = hasBorder ? 1.5 : 0;

  switch (status) {
    // Small filled circle: off
    case "off":
      return (
        <svg viewBox="0 0 12 12" width={size} height={size}>
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 2.5 : 3}
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    // Ring (donut): standby
    case "standby":
      return (
        <svg viewBox="0 0 12 12" width={size} height={size}>
          <circle
            cx="6"
            cy="6"
            r="3.5"
            fill="none"
            stroke={fillColor}
            strokeWidth="2"
          />
          {hasBorder && (
            <circle
              cx="6"
              cy="6"
              r="4.75"
              fill="none"
              stroke={borderColor}
              strokeWidth={strokeWidth}
            />
          )}
        </svg>
      );

    // Filled circle: normal
    case "normal":
      return (
        <svg viewBox="0 0 12 12" width={size} height={size}>
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 4.5 : 5}
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    // Square shape: caution
    case "caution":
      return (
        <svg viewBox="0 0 12 12" width={size} height={size}>
          <rect
            x={hasBorder ? 1.5 : 1}
            y={hasBorder ? 1.5 : 1}
            width={hasBorder ? 9 : 10}
            height={hasBorder ? 9 : 10}
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    // Diamond shape: serious (rotated square)
    case "serious":
      return (
        <svg viewBox="0 0 12 12" width={size} height={size}>
          <polygon
            points="6,1 11,6 6,11 1,6"
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    // Triangle pointing down: critical
    case "critical":
      return (
        <svg viewBox="0 0 12 12" width={size} height={size}>
          <polygon
            points="6,11 1,2 11,2"
            fill={fillColor}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        </svg>
      );

    default:
      return null;
  }
};

/**
 * StatusIndicator - Pure React status component
 *
 * Displays standardized status symbols following Astro UX design patterns.
 * Uses dual-coding (color + shape) for WCAG 2.1 AA accessibility compliance.
 *
 * @example
 * ```tsx
 * <StatusIndicator status="normal" label="Power System" />
 * <StatusIndicator status="caution" label="Battery Low" />
 * <StatusIndicator status="critical" label="Comm Loss" pulse />
 * ```
 */
export const StatusIndicator = memo(function StatusIndicator({
  status,
  label,
  small = false,
  pulse = false,
  bordered,
  className = "",
  onClick,
}: StatusIndicatorProps): React.ReactElement {
  const { tokens, mode } = useTheme();

  // Determine if we need borders (light theme or explicitly requested)
  const isLightTheme = mode === "light";
  const showBorder = bordered ?? isLightTheme;

  // Get colors from theme or use Astro defaults
  const fillColor = isLightTheme
    ? STATUS_COLORS_LIGHT_FILL[status]
    : tokens.colors.status[status] || STATUS_COLORS_DARK[status];
  const borderColor = showBorder
    ? STATUS_COLORS_LIGHT_BORDER[status]
    : undefined;

  const size = small ? 10 : 14;
  const shouldPulse = pulse || status === "critical";
  const isCircle =
    status === "off" || status === "standby" || status === "normal";

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={classNames("zendir-status-indicator", className)}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={onClick ? 0 : undefined}
      role="status"
      aria-label={`${STATUS_ARIA_LABELS[status]}${label ? `: ${label}` : ""}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: small ? 4 : 6,
        cursor: onClick ? "pointer" : "default",
        outline: "none",
      }}
    >
      {/* Status symbol with dual-coding */}
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          flexShrink: 0,
        }}
      >
        {/* Pulse animation ring */}
        {shouldPulse && (
          <span
            style={{
              position: "absolute",
              inset: -3,
              borderRadius: isCircle ? "50%" : 0,
              backgroundColor: fillColor,
              opacity: 0.3,
              animation: "statusPulse 2s ease-out infinite",
            }}
          />
        )}

        {/* Astro-compliant status shape */}
        {renderStatusShape(status, size, fillColor, borderColor)}
      </span>

      {/* Label with theme typography */}
      {label && (
        <span
          style={{
            fontSize: small
              ? tokens.typography.fontSize.xs
              : tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.medium,
            color: "inherit",
            whiteSpace: "nowrap",
            letterSpacing: tokens.typography.letterSpacing.normal,
          }}
        >
          {label}
        </span>
      )}

      {/* Keyframe animation — paused when prefers-reduced-motion is active */}
      <style>{`
        @keyframes statusPulse {
          0% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .zendir-status-indicator span[style*="statusPulse"] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
});
